import {
  RocketAPI,
  type SettingsSchemaOptionsIntegerItem,
  type SettingsSchemaOptionsStringItem,
  type Site,
  type ViewFileResponse,
} from '@portal/rocket-api-client';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { buildViewFileUrl, normalizeFilenameForView } from 'utils/fileManagerUrl';

import { useTranslation } from '@shared/i18n';
import { delay } from '@shared/utils/delay';
import { PaginatedListState, serializeParamsForQuery } from '@shared/utils/pagination';

import { AxiosService } from 'api/axiosService';

import { userProfileSites } from '../../tests/fixtures/sites';

export type SiteUser = NonNullable<Site['site_users']>[number];

export type SiteSettingsSchemaOption =
  | SettingsSchemaOptionsStringItem
  | SettingsSchemaOptionsIntegerItem;

export const useInfiniteSearchSites = ({
  search,
  perPage = 10,
  options,
}: {
  search: string;
  perPage?: number;
  options?: { enabled?: boolean };
}) => {
  const { t } = useTranslation();
  const normalizedSearch = search.trim();

  return useInfiniteQuery({
    queryKey: [
      ...RocketAPI.sites.getListSitesQueryKey(),
      'infinite-search',
      perPage,
      normalizedSearch,
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) =>
      RocketAPI.sites.listSites({
        page: pageParam,
        per_page: perPage,
        ...(normalizedSearch && { search: normalizedSearch }),
      }),
    getNextPageParam: lastPage => {
      const metadata = lastPage.metadata;
      const page = metadata?.page ?? 1;
      const pageSize = metadata?.page_size ?? perPage;
      const total = metadata?.total ?? 0;
      return page * pageSize < total ? page + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    ...options,
    meta: { errorMessage: t('data_fetching_error.could_not_search_sites') },
  });
};

export const useInfiniteSearchUserSites = ({
  search,
  perPage = 10,
  options,
}: {
  search: string;
  perPage?: number;
  options?: { enabled?: boolean };
}) => {
  const { t } = useTranslation();
  const normalizedSearch = search.trim();

  return useInfiniteQuery({
    queryKey: [
      ...RocketAPI.siteUsers.getListSiteUserSitesQueryKey(),
      'infinite-search',
      perPage,
      normalizedSearch,
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) =>
      RocketAPI.siteUsers.listSiteUserSites({
        page: pageParam,
        per_page: perPage,
        ...(normalizedSearch && { search: normalizedSearch }),
      }),
    getNextPageParam: lastPage => {
      const metadata = lastPage.metadata;
      const page = metadata?.page ?? 1;
      const pageSize = metadata?.page_size ?? perPage;
      const total = metadata?.total ?? 0;
      return page * pageSize < total ? page + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    ...options,
    meta: { errorMessage: t('data_fetching_error.could_not_search_sites') },
  });
};

export const siteDetailsQueryOptions = (siteId: string) =>
  RocketAPI.sites.getFindSiteQueryOptions(Number(siteId));

export const useSiteDetails = (siteId: string, enabled = true) =>
  RocketAPI.sites.useFindSite(Number(siteId), { query: { enabled } });

// TODO: replace with real endpoint when backend is ready
export const useUserProfileSites = <FilterKey extends string>(
  state: PaginatedListState<FilterKey>
) => {
  const serializedParams = serializeParamsForQuery(state);

  return useQuery({
    queryKey: ['user', 'sites', serializedParams],

    queryFn: async () => {
      await delay(2000);
      return userProfileSites;
    },
  });
};

// TODO: replace with real endpoint when backend is ready
export const useDeleteSiteUserForAllSites = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['delete-site-user'],

    mutationFn: async ({ userId: _userId }: { userId: string }) => {
      // await AxiosService.delete<{ success: boolean }>(`sites/users/${userId}`, {}),
      await delay(2000);
      return { data: { success: true } };
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: RocketAPI.siteUsers.getListSiteUserSitesQueryKey(),
      });
    },
  });
};

export const useRequestWpAdminSso = (siteId: string) =>
  useMutation({
    mutationFn: async () => RocketAPI.wordPress.generateSiteWpLoginLink(Number(siteId)),
  });

/**
 * The endpoint is a GET and generates a query, but we are using it as a mutation
 */
export const useRequestPhpMyAdminSso = (siteId: string) =>
  useMutation({
    mutationFn: async () => RocketAPI.sites.generateSitePmaLogin(Number(siteId)),
  });

// TODO: Use the generated hook once it supports the required path normalization.
export const useFileContents = (siteId: string, filename?: string, directory?: string) => {
  const { t } = useTranslation();
  const normalizedFilename = normalizeFilenameForView(filename);

  return useQuery({
    queryKey: RocketAPI.files.getViewSiteFileQueryKey(Number(siteId), {
      filename: normalizedFilename,
      directory,
    }),

    queryFn: async () => {
      const url = buildViewFileUrl(siteId, filename, directory);

      return AxiosService.get<ViewFileResponse>(url);
    },

    meta: { errorMessage: t('data_fetching_error.could_not_get_file_contents') },
  });
};

export const useSiteWpStatus = (siteId: string | number | null = null, enabled = true) => {
  return RocketAPI.wordPress.useFindSiteWpStatus(Number(siteId), {
    query: { enabled: enabled && !!siteId },
  });
};
